import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'
import type { AppLocale } from '@/config/locale'

const nullableString = z.string().nullable().optional()
const nullableNumber = z.number().nullable().optional()

const rankSchema = z
  .object({
    id: nullableNumber,
    name: nullableString,
    icon: nullableString,
    color: nullableString,
    min_points: nullableNumber,
    max_points: nullableNumber,
  })
  .passthrough()

const joinedPouleSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    description: nullableString,
    thumbnail_url: nullableString,
    hero_url: nullableString,
    requires_approval: z.boolean().optional(),
    members_count: nullableNumber,
    classification: nullableString,
    audience_type: nullableString,
    supports_teams: z.boolean().optional(),
    prediction_release_mode: nullableString,
    my_points: nullableNumber,
    my_position: nullableNumber,
  })
  .passthrough()

const joinedPoulesResponseSchema = z
  .object({
    data: z.array(joinedPouleSchema).optional().default([]),
    current_page: nullableNumber,
    last_page: nullableNumber,
    total: nullableNumber,
  })
  .passthrough()

const statsHighlightSchema = z
  .object({
    key: nullableString,
    label: nullableString,
    value: nullableString,
    hint: nullableString,
  })
  .passthrough()

const statsProgressRankSchema = z
  .object({
    id: nullableNumber,
    name: nullableString,
    icon: nullableString,
    color: nullableString,
    min_points: nullableNumber,
    max_points: nullableNumber,
    is_current: z.boolean().optional(),
    is_completed: z.boolean().optional(),
  })
  .passthrough()

const statsProgressSchema = z
  .object({
    current_rank_score: nullableNumber,
    next_rank: statsProgressRankSchema.nullable().optional(),
    points_to_next_rank: nullableNumber,
    is_max_rank: z.boolean().optional(),
    ladder: z.array(statsProgressRankSchema).optional().default([]),
  })
  .passthrough()

const skillSchema = z
  .object({
    has_data: z.boolean().optional(),
    score: nullableNumber,
    confidence: nullableNumber,
    rank: rankSchema.nullable().optional(),
    summary: z
      .object({
        title: nullableString,
        subtitle: nullableString,
      })
      .passthrough()
      .optional(),
    highlights: z.array(statsHighlightSchema).optional().default([]),
    progress: statsProgressSchema.optional(),
  })
  .passthrough()

const performanceSchema = z
  .object({
    eligible: z.boolean().optional(),
    percentile: nullableNumber,
    label: nullableString,
  })
  .passthrough()

const myStatsDashboardSchema = z
  .object({
    total_predictions: nullableNumber,
    total_points: nullableNumber,
    avg_points: nullableNumber,
    avg_form: nullableNumber,
    predictions_with_points: nullableNumber,
    last_prediction_at: nullableString,
    performance: performanceSchema.optional(),
    skill: skillSchema.optional(),
  })
  .passthrough()

export type JoinedPoulesResponse = z.infer<typeof joinedPoulesResponseSchema>
export type JoinedPoule = JoinedPoulesResponse['data'][number]
export type MyStatsDashboard = z.infer<typeof myStatsDashboardSchema>

async function fetchJoinedPoules(page = 1, perPage = 100): Promise<JoinedPoulesResponse> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  })
  const response = await api.get<unknown>(`/poules/joined?${params.toString()}`)
  const parsed = joinedPoulesResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[my-golero] Invalid joined poules payload', parsed.error.flatten())
    throw new Error('Mijn poules konden niet worden geladen.')
  }

  return parsed.data
}

async function fetchAllJoinedPoules(page = 1): Promise<JoinedPoulesResponse> {
  const firstPage = await fetchJoinedPoules(page)
  const currentPage = firstPage.current_page ?? page
  const lastPage = firstPage.last_page ?? currentPage

  if (lastPage <= currentPage) return firstPage

  const remainingPages = await Promise.all(
    Array.from({ length: lastPage - currentPage }, (_, index) =>
      fetchJoinedPoules(currentPage + index + 1),
    ),
  )

  return {
    ...firstPage,
    data: [firstPage, ...remainingPages].flatMap((result) => result.data),
  }
}

async function fetchMyStatsDashboard(locale: AppLocale): Promise<MyStatsDashboard> {
  const response = await api.get<unknown>('/users/me/stats/dashboard')
  const parsed = myStatsDashboardSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[my-golero] Invalid stats dashboard payload', parsed.error.flatten())
    throw new Error('Mijn stats konden niet worden geladen.')
  }

  return parsed.data
}

export function useJoinedPoules(page = 1, enabled = true) {
  return useQuery({
    queryKey: ['joined-poules', page],
    queryFn: () => fetchAllJoinedPoules(page),
    enabled,
    staleTime: 30_000,
  })
}

export function useMyStatsDashboard(locale: AppLocale, enabled = true) {
  return useQuery({
    queryKey: ['my-stats-dashboard', locale],
    queryFn: () => fetchMyStatsDashboard(locale),
    enabled,
    staleTime: 30_000,
  })
}
